<template>
  <CustomModal
    title="Please review and confirm selections"
    v-model:visible="isVisible"
    :width="'600px'"
    :force-close="props.forceClose"
    @cancel="handleCancel"
  >
    <template #default>
      <div class="flex flex-col">
        <div class="text-sm text_color leading-[18px] mb-6">
          Adding to <span class="font-semibold">{{ sequenceName }}</span>
        </div>

        <div class="max-h-[60vh] overflow-y-auto">
          <div v-if="validationResults" class="flex flex-col gap-6">
            <div
              v-for="group in validationGroupsData"
              :key="group.key"
              v-show="group.totalCount > 0"
            >
              <div class="flex items-center mb-3 gap-2">
                <ThemeImage
                  :icon="group.icon"
                  :alt="group.iconAlt"
                  class="size-6"
                />
                <span
                  class="text-lg font-semibold"
                  :style="{ color: group.textColor }"
                >
                  {{ group.count }}
                  of
                  {{ getTotalCategoriesCount(validationResults, isManualMode) }}
                  {{ group.actionText }}
                </span>
              </div>
              <a-collapse
                v-model:activeKey="activeCollapseKeys"
                :bordered="true"
                :accordion="true"
                expand-icon-position="end"
                class="validation-collapse"
              >
                <a-collapse-panel
                  v-for="category in group.categories"
                  :key="category.key"
                >
                  <template #header>
                    <div class="flex items-center gap-3">
                      <a-checkbox
                        v-if="!category.isReadOnly"
                        v-model:checked="selectedCategories[category.key]"
                        :disabled="
                          group.key === validationCategoryGroups.CANNOT_ADD
                        "
                        @click.stop
                      />
                      <span>{{ getCollapseHeader(category) }}</span>
                    </div>
                  </template>
                  <div>
                    <CustomTable
                      :headers="getValidationTableHeaders(category)"
                      :data="getTableData(category)"
                      :pagination="getTablePagination(category)"
                      :show-page-size="false"
                      :source="`validation_${category.key}`"
                      height="400px"
                      :show-border="false"
                      border-radius="0px"
                      :custom-column-render="getCustomColumnRender(category)"
                      @update:pagination="
                        (p) => getUpdateHandler(category.key)(p)
                      "
                    />

                    <PaginationHandler
                      v-if="isManualMode"
                      :pagination="getManualPagination(category.key)"
                      :allow-change-page-size="false"
                      @update:pagination="
                        (p) => getManualUpdateHandler(category.key)(p)
                      "
                    />
                  </div>
                </a-collapse-panel>
              </a-collapse>
            </div>
          </div>

          <SkeletonLoader v-else :page="loaderPages.VALIDATION_MODAL" />
        </div>
      </div>
    </template>

    <template #footer>
      <a-button @click="handleCancel" class="secondary_medium">
        Cancel
      </a-button>
      <a-button
        @click="handleAddToJourney"
        class="primary_medium ml-2"
        :loading="addingToJourney"
        :disabled="!hasSelectedCategories"
      >
        Confirm {{ totalSelectedCount }} selection(s)
      </a-button>
    </template>
  </CustomModal>
</template>

<script setup>
import { ref, watch, computed, reactive } from "vue";
import CustomModal from "@/src/components/CustomModal.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import PaginationHandler from "@/src/components/custom_table_components/PaginationHandler.vue";
import SequenceDisplayCell from "@/src/components/sequences_components/SequenceDisplayCell.vue";
import MultipleIssuesRenderer from "@/src/components/sequences_components/MultipleIssuesRenderer.vue";
import EmptyFieldDisplay from "@/src/components/EmptyFieldDisplay.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { message } from "ant-design-vue";
import {
  validationTypes,
  validationCategories,
  validationCategoryGroups,
  groupConfigs,
} from "@/src/constants/sequenceConstants.js";
import {
  getPaginatedEntities,
  getEntityCount,
  groupValidationCategories,
  getTotalCategoriesCount,
  getGroupCount,
  getValidationTableHeaders,
  formatEntitiesForTable,
  getValidationCustomColumnRender,
} from "@/src/utils/sequenceUtils.js";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import SequenceService from "@/src/api/SequenceService";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  validationResults: {
    type: Object,
    default: null,
  },
  sequenceName: {
    type: String,
    default: "",
  },
  sequenceId: {
    type: [String, Number],
    required: true,
  },
  validationType: {
    type: String,
    default: null,
  },
  forceClose: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:visible",
  "add-to-journey",
  "update-validation-results",
]);

const isVisible = ref(false);
const addingToJourney = ref(false);
const activeCollapseKeys = ref(null);

const categoryGroups = groupValidationCategories();

const paginationStates = reactive(
  Object.fromEntries(
    Object.keys(validationCategories).map((key) => [
      key,
      { current: 1, pageSize: 5, loading: false },
    ]),
  ),
);

const selectedCategories = reactive(
  Object.fromEntries(
    Object.entries(validationCategories).map(([key, config]) => [
      key,
      key === "existing_sequences" ? false : config.defaultSelected,
    ]),
  ),
);

const getCustomColumnRender = (category) => {
  return getValidationCustomColumnRender(category.key, {
    EmptyFieldDisplay,
    SequenceDisplayCell,
    MultipleIssuesRenderer,
    onSequenceSelected: handleSequenceSelection,
    validationResults: props.validationResults,
    currentPerson: null, // Not used in ValidationModal context
  });
};

// Check if this is manual mode (frontend pagination) or list mode (backend pagination)
const isManualMode = computed(
  () =>
    props.validationType === validationTypes.MANUAL ||
    props.validationType === validationTypes.EMAIL,
);

// Simplified pagination helpers
const createPagination = (categoryKey) => {
  const pagination = paginationStates[categoryKey];
  const category = validationCategories[categoryKey];

  return {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: getEntityCount(
      props.validationResults,
      category,
      isManualMode.value,
    ),
  };
};

// Helper methods for table data and pagination
const getTableData = (category) => {
  if (isManualMode.value) {
    return getPaginatedEntities(
      props.validationResults[category.dataKey],
      paginationStates[category.key],
    );
  }
  return formatEntitiesForTable(props.validationResults[category.dataKey]);
};

const getTablePagination = (category) => {
  return isManualMode.value ? null : createPagination(category.key);
};

const getManualPagination = (categoryKey) => {
  return createPagination(categoryKey);
};

const getUpdateHandler = (categoryKey) => {
  return updatePagination(categoryKey);
};

const getManualUpdateHandler = (categoryKey) => {
  return (newPagination) => {
    paginationStates[categoryKey].current = newPagination.current;
    paginationStates[categoryKey].pageSize = newPagination.pageSize;
  };
};

// Generate collapse header with count and title
const getCollapseHeader = (category) => {
  if (category.key === "complete") {
    return category.title;
  }

  const count = getEntityCount(
    props.validationResults,
    category,
    isManualMode.value,
  );
  return `${count} ${category.title}`;
};

const hasSelectedCategories = computed(() => {
  return Object.entries(validationCategories).some(([key, category]) => {
    return (
      selectedCategories[key] &&
      getEntityCount(props.validationResults, category, isManualMode.value) > 0
    );
  });
});

const totalSelectedCount = computed(() => {
  return Object.entries(validationCategories).reduce(
    (count, [key, category]) => {
      if (selectedCategories[key]) {
        count += getEntityCount(
          props.validationResults,
          category,
          isManualMode.value,
        );
      }
      return count;
    },
    0,
  );
});

const groupSummaries = computed(() => {
  const summaries = {};

  Object.values(validationCategoryGroups).forEach((groupKey) => {
    const groupCategories = categoryGroups[groupKey] || [];
    const totalCount = groupCategories.reduce((sum, category) => {
      return (
        sum +
        getEntityCount(props.validationResults, category, isManualMode.value)
      );
    }, 0);

    const selectedCount = groupCategories.reduce((sum, category) => {
      if (selectedCategories[category.key]) {
        return (
          sum +
          getEntityCount(props.validationResults, category, isManualMode.value)
        );
      }
      return sum;
    }, 0);

    summaries[groupKey] = { totalCount, selectedCount };
  });

  return summaries;
});

const validationGroupsData = computed(() => {
  return Object.values(validationCategoryGroups).map((groupKey) => {
    const config = groupConfigs[groupKey];
    const categories = (categoryGroups[groupKey] || [])
      .filter(
        (cat) =>
          getEntityCount(props.validationResults, cat, isManualMode.value) > 0,
      )
      .map((cat) => ({
        ...cat,
        isReadOnly: ["complete", "existing_sequences"].includes(cat.key),
      }));

    return {
      key: groupKey,
      ...config,
      categories,
      totalCount: groupSummaries.value[groupKey]?.totalCount || 0,
      count: getGroupCount(
        props.validationResults,
        groupKey,
        isManualMode.value,
      ),
    };
  });
});

const getCategoryPageParam = (categoryKey) => {
  const pageParamMap = {
    complete: "complete_entities_page",
    ongoing_sequences: "ongoing_sequences_page",
    completed_sequences: "completed_sequences_page",
    empty_email: "empty_email_page",
    empty_linkedin: "empty_linkedin_page",
    existing_sequences: "existing_sequences_page",
    multiple_invalid: "multiple_invalid_page",
  };
  return pageParamMap[categoryKey];
};

const updatePagination = (categoryKey) => async (newPagination) => {
  const pagination = paginationStates[categoryKey];
  pagination.loading = true;
  const oldPageSize = pagination.pageSize;

  // Update pagination state directly
  pagination.current = newPagination.current;
  pagination.pageSize = newPagination.pageSize;

  // Sync page size across all pagination objects if changed
  if (oldPageSize !== newPagination.pageSize) {
    Object.values(paginationStates).forEach((paginationState) => {
      paginationState.pageSize = newPagination.pageSize;
    });
  }

  try {
    if (isManualMode.value) return;

    const pageParam = getCategoryPageParam(categoryKey);
    if (!pageParam) {
      console.error(
        `No page parameter mapping found for category: ${categoryKey}`,
      );
      return;
    }

    const validationPayload = {
      sequence_id: props.sequenceId,
      validation_type: props.validationType,
      page_size: newPagination.pageSize,
      user_id: localStorage.getItem("userId"),
      empty_linkedin_page: 1,
      empty_email_page: 1,
      completed_sequences_page: 1,
      ongoing_sequences_page: 1,
      complete_entities_page: 1,
      existing_sequences_page: 1,
      multiple_invalid_page: 1,
    };

    validationPayload[pageParam] = newPagination.current;

    if (props.validationResults?.list_id) {
      validationPayload.list_id = props.validationResults.list_id;
    }

    const response = await SequenceService.validateEntities(validationPayload);

    if (response?.success) {
      const category = validationCategories[categoryKey];
      const newValidationResults = { ...props.validationResults };

      if (response[category.dataKey]) {
        newValidationResults[category.dataKey] = response[category.dataKey];
      }

      if (response.totals) {
        newValidationResults.totals = {
          ...newValidationResults.totals,
          ...response.totals,
        };
      }

      emit("update-validation-results", newValidationResults);
    } else {
      message.error("Failed to fetch page data");
      console.error("Failed to fetch validation page data:", response);
    }
  } catch (error) {
    message.error("An error occurred while fetching page data");
    console.error("Error fetching validation page data:", error);
  } finally {
    pagination.loading = false;
  }
};

const resetModalState = () => {
  // Reset all pagination states
  Object.values(paginationStates).forEach((pagination) => {
    pagination.current = 1;
    pagination.pageSize = 5;
    pagination.loading = false;
  });

  // Reset category selections
  Object.entries(validationCategories).forEach(([key, config]) => {
    selectedCategories[key] =
      key === "existing_sequences" ? false : config.defaultSelected;
  });

  activeCollapseKeys.value = null;
};

watch(
  () => props.visible,
  (newValue) => {
    isVisible.value = newValue;
    if (newValue) {
      resetModalState();
    } else {
      addingToJourney.value = false;
    }
  },
  { immediate: true },
);

watch(
  () => isVisible.value,
  (newValue) => {
    emit("update:visible", newValue);
  },
);

const handleCancel = () => {
  isVisible.value = false;
};

const handleAddToJourney = () => {
  addingToJourney.value = true;

  const genericData = {
    selectedCategories: selectedCategories,
    validationResults: props.validationResults,
    validationType: props.validationType,
    sequenceId: props.sequenceId,
  };

  emit("add-to-journey", genericData);
};

const handleSequenceSelection = (updatedRow) => {
  if (!props.validationResults) return;

  Object.keys(validationCategories).forEach((categoryKey) => {
    const category = validationCategories[categoryKey];
    const dataArray = props.validationResults[category.dataKey];

    if (dataArray && Array.isArray(dataArray)) {
      const rowIndex = dataArray.findIndex(
        (row) =>
          row.id === updatedRow.id ||
          (row.email === updatedRow.email &&
            row.display_name === updatedRow.display_name),
      );

      if (rowIndex > -1) {
        const updatedDataArray = [...dataArray];
        updatedDataArray[rowIndex] = {
          ...updatedDataArray[rowIndex],
          ...updatedRow,
        };

        const newValidationResults = {
          ...props.validationResults,
          [category.dataKey]: updatedDataArray,
        };

        emit("update-validation-results", newValidationResults);
        return;
      }
    }
  });
};
</script>

<style scoped>
:deep(.ant-collapse) {
  background-color: transparent;
  color: var(--text-color);
  border: none;
}

:deep(.ant-collapse-header) {
  color: var(--text-color) !important;
  background-color: var(--white-opacity-2);
  border-radius: 8px 8px 0 0 !important;
}

:deep(.ant-collapse-content-box) {
  padding: 0 !important;
}

:deep(.ant-collapse-item) {
  border: 1px solid var(--white-opacity-8);
  border-radius: 8px;
  margin-bottom: 8px;
}

:deep(.ant-collapse-item:last-child) {
  border-radius: 8px !important;
}
</style>
